import { createRouter, createWebHistory } from "vue-router";
import { useMainStore } from "@/stores/store";
import UserService from "@/services/UserService";
import { checkSubscription } from "@/util/KyanoSubscription.js";

const routes = [
  {
    path: "/:workspace_id",
    name: "Dashboard",
    component: () =>
      import(/* webpackChunkName: "main" */ "../views/home/Overview.vue")
  },
  {
    path: "/",
    component: () =>
      import(/* webpackChunkName: "main" */ "../views/home/Overview.vue")
  },
  {
    path: "/:catchAll(.*)",
    component: () => import(/* webpackChunkName: "404" */ "../views/404.vue")
  },

  {
    path: "/example",
    component: () =>
      import(/* webpackChunkName: "main" */ "../components/ExampleControls.vue")
  },
  {
    path: "/accept-invite",
    name: "AcceptInvite",
    component: () =>
      import(/* webpackChunkName: "main" */ "../views/auth/InvitationAccepted.vue")
  },
  {
    path: "/login",
    component: () =>
      import(/* webpackChunkName: "login" */ "../views/auth/Login.vue")
  },
  {
    path: "/password-reset",
    component: () =>
      import(/* webpackChunkName: "pwreset" */ "../views/auth/PasswordReset.vue")
  },
  {
    path: "/password-set",
    component: () =>
      import(/* webpackChunkName: "pwreset" */ "../views/auth/PasswordSet.vue")
  },

  {
    path: "/register",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/auth/Register.vue")
  },

  {
    path: "/admin/statuspage",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/admin/statuspage/Overview.vue")
  },

  {
    path: "/comment/:hash",
    component: () =>
      import(/* webpackChunkName: "register" */ "../views/admin/comments/View.vue")
  },

  {
    path: "/esign",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/admin/esign/__template.vue"),
    children: [
      {
        path: ":hash",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/esign/Signview.vue")
      },
      {
        path: "login",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/esign/SignviewAuth.vue")
      },
      {
        path: "overview",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/esign/Overview.vue")
      }
    ]
  },
  // {
  //   path: "/example",
  //   component: () =>
  //     import(/* webpackChunkName: "settings" */ "../views/example/__template.vue"),
  //   children: [
  //     {
  //       path: "",
  //       component: () =>
  //         import(/* webpackChunkName: "settings" */ "../views/example/View.vue")
  //     }
  //   ]
  // },
  {
    path: "/checkout",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/checkout/checkout.vue")
  },

  {
    path: "/checkout/payment-success",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/checkout/checkout-payment-success.vue")
  },

  {
    path: "/checkout/payment-failed",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/checkout/checkout-payment-failed.vue")
  },

  {
    path: "/workspace-setup",
    name: "workspace-setup",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/workspaces/Setup.vue")
  },
  {
    path: "/workspaces",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/workspaces/Workspaces.vue")
  },

  {
    path: "/settings",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/GeneralSettings.vue")
  },
  {
    path: "/members",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/UserSettings.vue")
  },
  {
    path: "/usage",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/UsageSettings.vue")
  },
  {
    path: "/billing",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/BillingSettings.vue")
  },
  {
    path: "/upgrade",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/UpgradeSettings.vue")
  },
  {
    path: "/trash",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/TrashSettings.vue")
  },

  {
    path: "/account",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/MySettings.vue")
  },
  {
    path: "/notifications",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/settings/NotificationsSettings.vue")
  },

  {
    path: "/app",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/apps/__template.vue"),
    children: [
      {
        path: ":id/data",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/EditData.vue")
      },
      {
        path: ":id/layout",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/EditLayout.vue")
      },
      {
        path: ":id/permissions",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/EditPermissions.vue")
        // meta: { requiredModules: ["permissions"] }
      },
      {
        path: ":id/flows",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/EditFlows.vue"),
        meta: { requiredModules: ["flows"] }
      },
      {
        path: ":id/settings",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/EditSettings.vue")
      }
    ]
  },
  {
    path: "/#",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/apps/__template.vue"),
    children: [
      {
        path: "/checkout",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/modules/checkout/Checkout.vue")
      },

      {
        path: "/esign",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/apps/modules/esign/__template.vue"),
        children: [
          {
            path: ":hash",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/apps/modules/esign/Signview.vue")
          },
          {
            path: "login",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/apps/modules/esign/SignviewAuth.vue")
          },
          {
            path: "overview",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/apps/modules/esign/Overview.vue")
          }
        ]
      }
    ]
  },

  {
    path: "/:workspace_id/cookiebanners",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/cookiebanners/__template.vue"),
    children: [
      {
        path: "",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/cookiebanners/Overview.vue")
      },
      {
        path: ":id",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/cookiebanners/Edit.vue")
      }
    ]
  },

  {
    path: "/:workspace_id/cookiebanners/:id/preview",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/cookiebanners/Preview.vue")
  },

  // Admin/staff modules

  {
    path: "/admin",
    component: () =>
      import(/* webpackChunkName: "apps" */ "../views/admin/__template.vue"),
    meta: { requiresStaff: true },
    children: [
      {
        path: "",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "finance",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/Finance.vue"),
        meta: { requiresStaff: true }
      },

      {
        path: "settings",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/settings/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "inbox",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/inbox/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "inbox/:id",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/inbox/View.vue")
      },
      {
        path: "settings/esign",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/esign/__template.vue"),
        children: [
          {
            path: "",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/esign/Settings.vue"),
            meta: { requiresStaff: true }
          }
        ]
      },
      {
        path: "settings/mails",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/mails/__template.vue"),
        meta: { requiresStaff: true },
        children: [
          {
            path: "",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/Settings.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: "templates",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/Templates.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: "imap",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/Imap.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: "ticket-statuses",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/TicketStatuses.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: "departments",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/Departments.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: "blacklist",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/mails/Blacklist.vue"),
            meta: { requiresStaff: true }
          }
        ]
      },

      {
        path: "settings/employees",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/employees/__template.vue"),
        meta: { requiresStaff: true },
        children: [
          {
            path: "",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/employees/Overview.vue"),
            meta: { requiresStaff: true }
          },
          {
            path: ":id",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/employees/View.vue"),
            meta: { requiresStaff: true }
          }
        ]
      },

      {
        path: "users",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/debtors/__template.vue"),
        meta: { requiresStaff: true },
        children: [
          {
            path: "",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/debtors/Overview.vue"),
            meta: { requiresStaff: true, props: true }
          },
          {
            path: ":id",
            component: () =>
              import(/* webpackChunkName: "settings" */ "../views/admin/debtors/View.vue"),
            meta: { requiresStaff: true }
          }
        ]
      },
      {
        path: "roadmap",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/roadmap/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "store",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/Store.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "store/software-packages",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/products/SoftwarePackages.vue"),
        meta: { requiresStaff: true }
      },

      {
        path: "products",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/products/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "products/:id",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/products/View.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "product-categories",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/product-categories/Overview.vue"),
        meta: { requiresStaff: true }
      },
      {
        path: "products-categories/:id",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/product-categories/View.vue"),
        meta: { requiresStaff: true }
      }
    ]
  },

  {
    path: "/admin/sales",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/admin/sales/__template.vue"),
    children: [
      {
        path: "",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/sales/Overview.vue")
      },

      {
        path: ":id",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/sales/View.vue")
      }
    ]
  },

  {
    path: "/admin/docs",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/admin/docs/__template.vue"),
    children: [
      {
        path: "",
        component: () =>
          import(/* webpackChunkName: "settings" */ "../views/admin/docs/Overview.vue")
      }
    ]
  },

  {
    path: "/admin/docs/:id",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/admin/docs/View.vue")
  },

  {
    path: "/admin/docs/create",
    component: () =>
      import(/* webpackChunkName: "settings" */ "../views/admin/docs/Create.vue")
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,

  scrollBehavior(to, from, savedPosition) {
    // If the user manually scrolled and then went back, restore the position
    if (savedPosition) {
      return savedPosition;
    } else {
      // Always scroll to the top when navigating
      return { top: 0 };
    }
  }
});
// router.beforeEach((to, from, next) => {
//   const store = useMainStore();
//   const user = store.user;

//   if (!user || !user.id) {
//     // Wacht op App.vue die de user ophaalt
//     return next();
//   }

//   if (to.meta.requiresStaff && !user.is_staff) {
//     return next("/");
//   }

//   next();
// });

router.beforeEach((to, from, next) => {
  const store = useMainStore();
  const user = store.user;

  // 1) staff-only check
  // if (to.meta.requiresStaff && !user?.is_staff) {
  //   return next("/");
  // }

  // 2) module-check
  const required = to.meta.requiredModules;
  if (Array.isArray(required) && required.length > 0) {
    const hasAccess = checkSubscription(required);
    if (!hasAccess) {
      // geen toegang → blijf op dezelfde route, maar voeg ?plans toe
      return next({
        name: to.name,
        params: to.params,
        query: {
          ...to.query,
          plans: required.join(",") // of gewoon 'true' als je niet de lijst nodig hebt
        }
      });
    }
  }

  next();
});

// router.beforeEach(async (to, from, next) => {
//   const store = useMainStore();
//   const user = store.user;

//   console.log("Router guard triggered", user);
//   if (!user.id) {
//     await UserService.getCurrentUser();
//   }

//   const loadedUser = store.user;

//   if (!loadedUser) {
//     return next();
//   }

//   if (to.meta.requiresStaff) {
//     if (!loadedUser.is_staff) {
//       return next("/");
//     }
//   }

//   next();
// });

export default router;
